import * as THREE from 'three';
import { GameLoop } from './GameLoop';
import { Renderer } from '../render/Renderer';
import { PhysicsWorld } from '../physics/PhysicsWorld';
import { InputManager } from '../player/InputManager';
import { PlayerController } from '../player/PlayerController';
import { AudioManager } from '../audio/AudioManager';
import { InteractionSystem } from '../interaction/InteractionSystem';
import { HUD } from '../ui/HUD';
import { SaveSystem } from '../persistence/SaveSystem';
import { eventBus } from '../events/EventBus';
import type { GameState } from '../types';

export class Engine {
  private renderer: Renderer;
  private gameLoop: GameLoop;
  private physics: PhysicsWorld;
  private inputManager: InputManager;
  private playerController: PlayerController;
  private audioManager: AudioManager;
  private interactionSystem: InteractionSystem;
  private hud: HUD;
  public saveSystem: SaveSystem;

  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;

  state: GameState = {
    isPaused: false,
    isPlaying: false,
    currentScene: '',
    time: 0,
    deltaTime: 0,
  };

  constructor(container: HTMLElement) {
    this.renderer = new Renderer(container);
    this.gameLoop = new GameLoop(this.update.bind(this), this.render.bind(this));
    this.physics = new PhysicsWorld();
    this.inputManager = new InputManager();
    this.playerController = new PlayerController(this.inputManager);
    this.audioManager = new AudioManager();
    this.interactionSystem = new InteractionSystem();
    this.hud = new HUD(container);
    this.saveSystem = new SaveSystem();

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    eventBus.on('game:pause', () => {
      this.state.isPaused = true;
      this.gameLoop.stop();
    });

    eventBus.on('game:resume', () => {
      this.state.isPaused = false;
      this.gameLoop.start();
    });

    eventBus.on('game:start', () => {
      this.state.isPlaying = true;
      this.gameLoop.start();
    });

    eventBus.on('interaction:available', (data) => {
      this.hud.showInteractionPrompt(data.label);
    });

    eventBus.on('interaction:unavailable', () => {
      this.hud.hideInteractionPrompt();
    });
  }

  async init(): Promise<void> {
    // Inicializar física
    await this.physics.init();

    // Crear escena y cámara
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );

    // Configurar renderizador
    this.renderer.init(this.scene, this.camera);

    // Inicializar sistemas
    this.inputManager.init();
    this.playerController.init(this.camera, this.physics);
    this.audioManager.init();
    this.interactionSystem.init(this.camera, this.scene);
    this.hud.init();

    // Cargar escena inicial
    await this.loadScene('radio-station');

    // Manejar redimensionamiento
    window.addEventListener('resize', this.onResize.bind(this));

    console.log('[Engine] Inicializado correctamente');
  }

  private async loadScene(sceneName: string): Promise<void> {
    if (!this.scene) return;

    eventBus.emit('scene:unload', this.state.currentScene);

    // Limpiar escena actual
    while (this.scene.children.length > 0) {
      this.scene.remove(this.scene.children[0]);
    }

    // Aquí se cargaría la escena específica
    // Por ahora, crear escena básica
    this.createBasicScene();

    this.state.currentScene = sceneName;
    eventBus.emit('scene:load', sceneName);

    console.log(`[Engine] Escena cargada: ${sceneName}`);
  }

  private createBasicScene(): void {
    if (!this.scene) return;

    // Luz ambiental tenue
    const ambientLight = new THREE.AmbientLight(0x1a1a2e, 0.3);
    this.scene.add(ambientLight);

    // Luz principal (lámpara de escritorio)
    const pointLight = new THREE.PointLight(0xffaa44, 1, 15);
    pointLight.position.set(2, 2.5, 1);
    pointLight.castShadow = true;
    this.scene.add(pointLight);

    // Luz de relleno fría
    const fillLight = new THREE.DirectionalLight(0x4444ff, 0.1);
    fillLight.position.set(-5, 5, -5);
    this.scene.add(fillLight);

    // Niebla para atmósfera
    this.scene.fog = new THREE.FogExp2(0x0a0a0f, 0.08);
    this.scene.background = new THREE.Color(0x0a0a0f);

    // Crear geometría básica de la sala
    this.createRoom();
  }

  private createRoom(): void {
    if (!this.scene) return;

    const roomWidth = 12;
    const roomHeight = 4;
    const roomDepth = 10;

    // Materiales
    const wallMaterial = new THREE.MeshStandardMaterial({
      color: 0x2a2a35,
      roughness: 0.9,
      metalness: 0.1,
    });

    const floorMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a1a20,
      roughness: 0.95,
      metalness: 0.05,
    });

    // Suelo
    const floorGeometry = new THREE.PlaneGeometry(roomWidth, roomDepth);
    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.scene.add(floor);

    // Techo
    const ceiling = new THREE.Mesh(floorGeometry, wallMaterial);
    ceiling.rotation.x = Math.PI / 2;
    ceiling.position.y = roomHeight;
    this.scene.add(ceiling);

    // Paredes
    const wallGeometry = new THREE.PlaneGeometry(roomWidth, roomHeight);

    const backWall = new THREE.Mesh(wallGeometry, wallMaterial);
    backWall.position.set(0, roomHeight / 2, -roomDepth / 2);
    this.scene.add(backWall);

    const frontWall = new THREE.Mesh(wallGeometry, wallMaterial);
    frontWall.position.set(0, roomHeight / 2, roomDepth / 2);
    frontWall.rotation.y = Math.PI;
    this.scene.add(frontWall);

    const sideWallGeometry = new THREE.PlaneGeometry(roomDepth, roomHeight);

    const leftWall = new THREE.Mesh(sideWallGeometry, wallMaterial);
    leftWall.position.set(-roomWidth / 2, roomHeight / 2, 0);
    leftWall.rotation.y = Math.PI / 2;
    this.scene.add(leftWall);

    const rightWall = new THREE.Mesh(sideWallGeometry, wallMaterial);
    rightWall.position.set(roomWidth / 2, roomHeight / 2, 0);
    rightWall.rotation.y = -Math.PI / 2;
    this.scene.add(rightWall);

    // Añadir colisiones al mundo físico
    this.physics.addStaticBox(
      { x: 0, y: -0.5, z: 0 },
      { x: roomWidth, y: 1, z: roomDepth }
    );
    this.physics.addStaticBox(
      { x: 0, y: roomHeight + 0.5, z: 0 },
      { x: roomWidth, y: 1, z: roomDepth }
    );
    this.physics.addStaticBox(
      { x: 0, y: roomHeight / 2, z: -roomDepth / 2 - 0.5 },
      { x: roomWidth, y: roomHeight, z: 1 }
    );
    this.physics.addStaticBox(
      { x: 0, y: roomHeight / 2, z: roomDepth / 2 + 0.5 },
      { x: roomWidth, y: roomHeight, z: 1 }
    );
    this.physics.addStaticBox(
      { x: -roomWidth / 2 - 0.5, y: roomHeight / 2, z: 0 },
      { x: 1, y: roomHeight, z: roomDepth }
    );
    this.physics.addStaticBox(
      { x: roomWidth / 2 + 0.5, y: roomHeight / 2, z: 0 },
      { x: 1, y: roomHeight, z: roomDepth }
    );

    // Crear mesa de radio
    this.createRadioDesk();

    // Crear radio interactiva
    this.createRadio();
  }

  private createRadioDesk(): void {
    if (!this.scene) return;

    const deskMaterial = new THREE.MeshStandardMaterial({
      color: 0x3d2b1f,
      roughness: 0.8,
      metalness: 0.2,
    });

    // Superficie de la mesa
    const deskTop = new THREE.Mesh(
      new THREE.BoxGeometry(3, 0.1, 1.5),
      deskMaterial
    );
    deskTop.position.set(0, 1, -3);
    deskTop.castShadow = true;
    deskTop.receiveShadow = true;
    this.scene.add(deskTop);

    // Patas de la mesa
    const legGeometry = new THREE.BoxGeometry(0.1, 1, 0.1);
    const positions = [
      { x: -1.4, z: -3.7 },
      { x: 1.4, z: -3.7 },
      { x: -1.4, z: -2.3 },
      { x: 1.4, z: -2.3 },
    ];

    positions.forEach((pos) => {
      const leg = new THREE.Mesh(legGeometry, deskMaterial);
      leg.position.set(pos.x, 0.5, pos.z);
      if (this.scene) {
        this.scene.add(leg);
      }
    });

    // Colisión de la mesa
    this.physics.addStaticBox(
      { x: 0, y: 0.5, z: -3 },
      { x: 3, y: 1, z: 1.5 }
    );
  }

  private createRadio(): void {
    if (!this.scene) return;

    // Cuerpo de la radio
    const radioBody = new THREE.Mesh(
      new THREE.BoxGeometry(0.8, 0.5, 0.4),
      new THREE.MeshStandardMaterial({
        color: 0x1a1a1a,
        roughness: 0.6,
        metalness: 0.4,
      })
    );
    radioBody.position.set(0, 1.3, -3);
    radioBody.castShadow = true;
    this.scene.add(radioBody);

    // Dial de frecuencia
    const dial = new THREE.Mesh(
      new THREE.CylinderGeometry(0.08, 0.08, 0.05, 16),
      new THREE.MeshStandardMaterial({
        color: 0x333333,
        roughness: 0.3,
        metalness: 0.8,
      })
    );
    dial.rotation.x = Math.PI / 2;
    dial.position.set(0.25, 1.35, -2.79);
    this.scene.add(dial);

    // Altavoz
    const speaker = new THREE.Mesh(
      new THREE.CircleGeometry(0.15, 16),
      new THREE.MeshStandardMaterial({
        color: 0x0a0a0a,
        roughness: 0.9,
      })
    );
    speaker.position.set(-0.2, 1.3, -2.79);
    this.scene.add(speaker);

    // Luz de encendido
    const powerLight = new THREE.PointLight(0xff0000, 0.5, 2);
    powerLight.position.set(0.35, 1.4, -2.8);
    this.scene.add(powerLight);

    // Registrar como objeto interactivo
    this.interactionSystem.register({
      id: 'radio',
      label: 'Encender radio',
      position: { x: 0, y: 1.3, z: -3 },
      radius: 2,
      isActive: true,
      onInteract: () => {
        eventBus.emit('radio:tune', 93.1);
        this.audioManager.play('radio-static');
        if (powerLight) {
          powerLight.color.setHex(0x00ff00);
        }
      },
    });
  }

  private update(deltaTime: number): void {
    if (this.state.isPaused) return;

    this.state.time += deltaTime;
    this.state.deltaTime = deltaTime;

    // Actualizar sistemas
    this.playerController.update(deltaTime);
    this.physics.update(deltaTime);
    this.interactionSystem.update();
    this.audioManager.update(deltaTime);

    // Actualizar HUD
    this.hud.update(this.state);
  }

  private render(): void {
    if (this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  private onResize(): void {
    if (this.camera) {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
    }
    this.renderer.resize(window.innerWidth, window.innerHeight);
  }

  start(): void {
    this.state.isPlaying = true;
    this.gameLoop.start();
    console.log('[Engine] Juego iniciado');
  }

  pause(): void {
    eventBus.emit('game:pause', undefined);
  }

  resume(): void {
    eventBus.emit('game:resume', undefined);
  }

  dispose(): void {
    this.gameLoop.stop();
    this.inputManager.dispose();
    this.audioManager.dispose();
    this.renderer.dispose();
    this.physics.dispose();
    this.hud.dispose();
    eventBus.clear();
  }
}
